import { Navigate, useLocation } from "react-router";
import { routePermissions, type Route } from "@/auth/routes";
import { usePermissions } from "@/auth/permissions";
import UnauthorizedPage from "@/auth/unauthorized-page";
import LoadingIndicator from "@/ui/loading-indicator";
import { useAuth } from "react-oidc-context";
import type { JSX } from "react";

interface RequireAuthProps {
  children: JSX.Element;
}

/**
 * A component that requires the user to be authenticated and permitted.
 * Modified from https://gist.github.com/mjackson/d54b40a094277b7afdd6b81f51a0393f
 */
export default function RequireAuth({
  children,
}: RequireAuthProps): JSX.Element | null {
  const auth = useAuth();
  const location = useLocation();
  const { loading, can } = usePermissions();

  if (!auth.isAuthenticated) return <Navigate to="/login" />;
  if (!auth.user) return null;

  // Nothing is permitted until the answer arrives, so waiting is the difference
  // between an unauthorised page and a page that flashes one.
  if (loading) return <LoadingIndicator />;

  const required = routePermissions[location.pathname as Route];
  if (!required.every((permission) => can(permission))) {
    return <UnauthorizedPage />;
  }
  return children;
}
